import { useState } from 'react'
import { GRAMS_PER_OUNCE, formatAmount, money } from '../../../shared/stash'
import { CATEGORIES, iconFor, stash, toCents, type Batch, type Pack } from '../../lib/stash'
import { floatText, sound, buzz, confetti } from '../../lib/fun'
import Sheet from './Sheet'
import Icon from '../Icon'

/** Everything he has bought, and how much of each is bagged and sold. */
export default function Batches({ batches, packs, onChange }: { batches: Batch[]; packs: Pack[]; onChange: () => Promise<void> }) {
  const [adding, setAdding] = useState(false)
  const [openId, setOpenId] = useState<string | null>(null)
  const [showArchived, setShowArchived] = useState(false)
  const live = batches.filter((b) => !b.archived)
  const archived = batches.filter((b) => b.archived)
  const open = batches.find((b) => b.id === openId) ?? null

  return (
    <div className="batches">
      <h1 className="page-title">My stash <small>{live.length} {live.length === 1 ? 'batch' : 'batches'}</small></h1>
      <button type="button" className="cta" onClick={() => setAdding(true)}><Icon name="plus" /> New batch</button>

      {!batches.length && <p className="muted center">Bought something? Log it here — what it is, how much, what you paid.</p>}

      {live.map((b) => <BatchCard key={b.id} b={b} onOpen={() => setOpenId(b.id)} />)}

      {archived.length > 0 && (
        <button type="button" className="link-btn" onClick={() => setShowArchived(!showArchived)}>
          {showArchived ? 'Hide' : 'Show'} {archived.length} archived
        </button>
      )}
      {showArchived && archived.map((b) => <BatchCard key={b.id} b={b} onOpen={() => setOpenId(b.id)} />)}

      {adding && <NewBatch onClose={() => setAdding(false)} onDone={async (id) => { setAdding(false); await onChange(); setOpenId(id) }} />}
      {open && <BatchSheet b={open} packs={packs.filter((p) => p.batch_id === open.id)} onClose={() => setOpenId(null)} onChange={onChange} />}
    </div>
  )
}

function BatchCard({ b, onOpen }: { b: Batch; onOpen: () => void }) {
  const pct = (n: number) => `${Math.min(100, (n / b.amount_milli) * 100)}%`
  const loose = b.amount_milli - b.packed_milli - b.waste_milli
  return (
    <button type="button" className={`batch ${b.archived ? 'is-archived' : ''}`} onClick={onOpen}>
      <div className="batch-top">
        <span className="stack-icon"><Icon name={iconFor(b.category)} /></span>
        <div className="batch-name">
          <b>{b.name}{b.finished_at && <span className="done-pill"><Icon name="check" size={12} /> Done</span>}</b>
          <span className="muted small">{formatAmount(b.amount_milli, b.unit)} · paid {money(b.cost_cents)}</span>
        </div>
        <div className="batch-money">
          <b className="up">{money(b.sold_cents)}</b>
          <small className="muted">sold</small>
        </div>
      </div>
      <div className="meter" aria-hidden="true">
        <span className="m-sold" style={{ width: pct(b.sold_milli) }} />
        <span className="m-bagged" style={{ width: pct(b.packed_milli - b.sold_milli) }} />
      </div>
      <div className="meter-key small">
        <span><i className="dot sold" />{b.sold_count} sold</span>
        <span><i className="dot bagged" />{b.ready_count} bagged</span>
        {b.finished_at
          ? b.waste_milli > 0 && <span><i className="dot loose" />{formatAmount(b.waste_milli, b.unit)} written off</span>
          : <span><i className="dot loose" />{formatAmount(loose, b.unit)} loose</span>}
      </div>
    </button>
  )
}

function NewBatch({ onClose, onDone }: { onClose: () => void; onDone: (id: string) => Promise<void> }) {
  const [name, setName] = useState('')
  const [category, setCategory] = useState('flower')
  const [amount, setAmount] = useState('1')
  const [unit, setUnit] = useState<'oz' | 'g' | 'each'>('oz')
  const [cost, setCost] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const n = Number(amount)
  const amountMilli = Number.isFinite(n) && n > 0 ? Math.round(n * (unit === 'oz' ? GRAMS_PER_OUNCE : 1) * 1000) : 0
  const costCents = cost.trim() ? toCents(cost) : 0

  async function save() {
    if (!name.trim()) { setError('Give it a name.'); return }
    if (!amountMilli) { setError('How much did you get?'); return }
    if (costCents === null) { setError('What you paid is not a number.'); return }
    setBusy(true)
    setError(null)
    try {
      const r = await stash.createBatch({ name, category, unit: unit === 'each' ? 'each' : 'g', amountMilli, costCents })
      sound('badge'); buzz([20, 30, 20]); confetti(40)
      floatText(`+${r.xp} XP`, { x: innerWidth / 2, y: innerHeight / 3 })
      await onDone(r.batch.id)
    } catch (e) {
      setError((e as Error).message)
      setBusy(false)
    }
  }

  return (
    <Sheet title="New batch" onClose={onClose}>
      <label>
        What is it?
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Blue Dream" maxLength={80} autoFocus />
      </label>
      <div className="chips" role="group" aria-label="Kind">
        {CATEGORIES.map((c) => (
          <button key={c.key} type="button" className={`chip ${category === c.key ? 'on' : ''}`} onClick={() => setCategory(c.key)}>
            <Icon name={c.icon} size={18} /> {c.label}
          </button>
        ))}
      </div>
      <label>
        How much?
        <div className="amount-row">
          <input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
          <div className="seg">
            {(['oz', 'g', 'each'] as const).map((u) => (
              <button key={u} type="button" className={unit === u ? 'on' : ''} onClick={() => setUnit(u)}>{u}</button>
            ))}
          </div>
        </div>
      </label>
      {unit === 'oz' && amountMilli > 0 && <p className="muted small">= {(amountMilli / 1000).toFixed(2)} g</p>}
      <label>
        What did you pay?
        <div className="money-input"><span>$</span><input inputMode="decimal" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="0" /></div>
      </label>
      {error && <p className="error">{error}</p>}
      <button type="button" className="cta big" disabled={busy} onClick={save}><Icon name="box" /> {busy ? 'Saving…' : 'Add to my stash · +25 XP'}</button>
    </Sheet>
  )
}

const PRESETS_G = [
  { label: '1 g', milli: 1000 },
  { label: '⅛ · 3.5 g', milli: 3500 },
  { label: '¼ · 7 g', milli: 7000 },
  { label: '½ · 14 g', milli: 14000 },
  { label: '1 oz', milli: 28350 },
]

function BatchSheet({ b, packs, onClose, onChange }: { b: Batch; packs: Pack[]; onClose: () => void; onChange: () => Promise<void> }) {
  const loose = b.amount_milli - b.packed_milli - b.waste_milli
  // Less than one gram (or one piece) left is crumbs, not a package. That is
  // the moment to offer the wrap-up instead of the bagging form.
  const crumbs = loose > 0 && loose < 1000
  const [size, setSize] = useState(b.unit === 'each' ? '1' : '3.5')
  const sizeMilli = Math.round(Number(size) * 1000) || 0
  const fits = sizeMilli > 0 ? Math.floor(loose / sizeMilli) : 0
  const [count, setCount] = useState(String(Math.max(1, fits)))
  const [price, setPrice] = useState('')
  const [label, setLabel] = useState(b.name)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const c = Math.floor(Number(count)) || 0
  const priceCents = toCents(price)
  const each = sizeMilli > 0 ? Math.round((b.cost_cents * sizeMilli) / b.amount_milli) : 0
  const left = loose - c * sizeMilli

  function pickSize(milli: number) {
    setSize(String(milli / 1000))
    setCount(String(Math.max(1, Math.floor(loose / milli))))
  }

  async function bag() {
    if (!sizeMilli) { setError('Pick a size.'); return }
    if (c < 1) { setError('How many?'); return }
    if (left < 0) { setError('Not enough left for that many.'); return }
    if (priceCents === null || !price.trim()) { setError('Set a price.'); return }
    setBusy(true)
    setError(null)
    try {
      const r = await stash.split(b.id, { count: c, amountMilli: sizeMilli, priceCents, label })
      sound('badge'); buzz([20, 30, 20]); confetti(30 + r.made * 3)
      floatText(`+${r.xp} XP`, { x: innerWidth / 2, y: innerHeight / 3 })
      setPrice('')
      await onChange()
      setCount('1')
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  async function act(fn: () => Promise<unknown>) {
    setError(null)
    try { await fn(); await onChange() } catch (e) { setError((e as Error).message) }
  }

  const ready = packs.filter((p) => p.status === 'ready')

  return (
    <Sheet title={b.name} onClose={onClose}>
      <div className="facts">
        <div><small>Bought</small><b>{formatAmount(b.amount_milli, b.unit)}</b></div>
        <div><small>Paid</small><b>{money(b.cost_cents)}</b></div>
        {b.finished_at
          ? <div><small>Written off</small><b>{formatAmount(b.waste_milli, b.unit)}</b></div>
          : <div><small>Loose</small><b>{formatAmount(loose, b.unit)}</b></div>}
        <div><small>Sold</small><b className="up">{money(b.sold_cents)}</b></div>
      </div>

      {b.finished_at ? (
        <div className="done-banner">
          <Icon name="check" size={22} />
          <div>
            <b>Batch wrapped up</b>
            <span className="muted small">{b.waste_milli > 0 ? `${formatAmount(b.waste_milli, b.unit)} written off` : 'Nothing left over'}{b.ready_count > 0 ? ` · ${b.ready_count} still on the shelf` : ''}</span>
          </div>
          <button type="button" className="ghost small" onClick={() => act(() => stash.reopen(b.id))}>Reopen</button>
        </div>
      ) : !b.archived && (crumbs || loose === 0) && (
        <WrapUp b={b} loose={loose} onChange={onChange} />
      )}

      {loose > 0 && !crumbs && !b.archived && !b.finished_at && (
        <section className="bagger">
          <h3>Bag it up</h3>
          {b.unit === 'g' && (
            <div className="chips">
              {PRESETS_G.filter((p) => p.milli <= loose).map((p) => (
                <button key={p.milli} type="button" className={`chip ${sizeMilli === p.milli ? 'on' : ''}`} onClick={() => pickSize(p.milli)}>{p.label}</button>
              ))}
            </div>
          )}
          <div className="split-row">
            <label>Size ({b.unit === 'g' ? 'g' : 'each'})<input inputMode="decimal" value={size} onChange={(e) => setSize(e.target.value)} /></label>
            <label>How many<input inputMode="numeric" value={count} onChange={(e) => setCount(e.target.value)} /></label>
            <label>Price each<div className="money-input"><span>$</span><input inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="40" /></div></label>
          </div>
          <label>Label<input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={60} /></label>
          <p className={`math ${left < 0 ? 'down' : ''}`}>
            {c} × {formatAmount(sizeMilli || 0, b.unit)} = {formatAmount(Math.max(0, c * sizeMilli), b.unit)}
            {left >= 0 ? ` · ${formatAmount(left, b.unit)} left over` : ' · too many!'}
            {priceCents !== null && price.trim() !== '' && (
              <> <br />Costs you {money(each)} each → <b className="up">{money(priceCents - each)}</b> profit each, <b className="up">{money((priceCents - each) * c)}</b> total</>
            )}
          </p>
          {error && <p className="error">{error}</p>}
          <button type="button" className="cta big" disabled={busy} onClick={bag}><Icon name="bag" /> {busy ? 'Bagging…' : `Make ${c || ''} package${c === 1 ? '' : 's'}`}</button>
        </section>
      )}

      {loose > 0 && !crumbs && !b.archived && !b.finished_at && (
        <details className="wrap-later">
          <summary>Done with this batch? Wrap it up</summary>
          <WrapUp b={b} loose={loose} onChange={onChange} />
        </details>
      )}

      {ready.length > 0 && (
        <section>
          <h3>Bagged, not sold ({ready.length})</h3>
          <ul className="mini-list">
            {ready.map((p) => (
              <li key={p.id}>
                <span>{p.label} · {formatAmount(p.amount_milli, p.unit)} · {money(p.price_cents)}</span>
                {!b.finished_at && <button type="button" className="ghost small" onClick={() => act(() => stash.deletePackage(p.id))} title="Put it back in the batch">Unbag</button>}
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="sheet-actions">
        <button type="button" className="ghost" onClick={() => act(() => stash.updateBatch(b.id, { archived: !b.archived }))}>
          <Icon name="archive" size={18} /> {b.archived ? 'Unarchive' : 'Archive'}
        </button>
        {b.sold_count === 0 && (
          <button type="button" className="ghost danger" onClick={() => { if (confirmDelete(b.name)) void act(async () => { await stash.deleteBatch(b.id); onClose() }) }}>
            <Icon name="trash" size={18} /> Delete
          </button>
        )}
      </div>
    </Sheet>
  )
}

/**
 * Close a batch out. Whatever is loose either goes into one last package or is
 * written off as shake; either way the batch is marked done.
 */
function WrapUp({ b, loose, onChange }: { b: Batch; loose: number; onChange: () => Promise<void> }) {
  const [price, setPrice] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const priceCents = price.trim() ? toCents(price) : null

  async function finish(bagTheRest: boolean) {
    if (bagTheRest && priceCents === null) { setError('Set a price for the last bag.'); return }
    setBusy(true)
    setError(null)
    try {
      if (bagTheRest) await stash.split(b.id, { count: 1, amountMilli: loose, priceCents: priceCents!, label: `${b.name} (last bit)` })
      await stash.finish(b.id)
      sound('badge'); buzz([20, 30, 20]); confetti(50)
      floatText('Batch done!', { x: innerWidth / 2, y: innerHeight / 3 })
      await onChange()
    } catch (e) {
      setError((e as Error).message)
      setBusy(false)
    }
  }

  return (
    <section className="wrapup">
      <h3><Icon name="check" /> Wrap up this batch</h3>
      {loose === 0 ? (
        <p className="muted">Everything's bagged. Mark it done.</p>
      ) : (
        <>
          <p className="muted">{formatAmount(loose, b.unit)} left over. Bag it as one last package, or write it off.</p>
          <label>
            Price for the last bag
            <div className="money-input"><span>$</span><input inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="5" /></div>
          </label>
        </>
      )}
      {error && <p className="error">{error}</p>}
      {loose > 0 && (
        <button type="button" className="cta" disabled={busy} onClick={() => finish(true)}>
          <Icon name="bag" /> Bag the last {formatAmount(loose, b.unit)} and wrap up
        </button>
      )}
      <button type="button" className={loose > 0 ? 'ghost wide' : 'cta'} disabled={busy} onClick={() => finish(false)}>
        <Icon name="check" size={18} /> {loose > 0 ? `Write off ${formatAmount(loose, b.unit)}${b.unit === 'g' ? ' as shake' : ''}` : 'Wrap up batch'}
      </button>
    </section>
  )
}

function confirmDelete(name: string): boolean {
  try { return window.confirm(`Delete ${name}? This removes it and any unsold packages.`) } catch { return false }
}
